import { useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { RouteMap } from "./RouteMap";
import { LapProfileChart } from "./LapProfileChart";
import { ActivityRangeSelector, SegmentStatsPanel } from "./ActivityRangeSelector";
import { buildLapChartData } from "../lib/laps";
import { getActivitySegmentStats } from "../lib/api";
import { resolveAnalyticsSidebarStats } from "../lib/analyticsSidebarStats";
import { cadencePresentation, canonicalActivitySport } from "../lib/sportAnalyticsGate";
import {
  getRangeBounds,
  indicesFromDomainValues,
  isFullSelection,
  formatRangeSummary,
  sliceForIndices,
  distanceRangeFromIndices,
} from "../lib/rangeSelection";

export const AnalyticsView = ({ activity, samples, userFtp = null, unitSystem = "metric" }) => {
  const bounds = useMemo(
    () => getRangeBounds(samples, activity.start_time),
    [samples, activity.start_time]
  );

  const [mode, setMode] = useState(() => (bounds.hasDistance ? "distance" : "time"));
  const lastIndex = Math.max(samples.length - 1, 0);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(lastIndex);

  useEffect(() => {
    setStartIndex(0);
    setEndIndex(Math.max(samples.length - 1, 0));
    setMode(bounds.hasDistance ? "distance" : "time");
  }, [activity.id, samples.length, bounds.hasDistance]);

  const deferredStart = useDeferredValue(startIndex);
  const deferredEnd = useDeferredValue(endIndex);

  const isFull = isFullSelection(deferredStart, deferredEnd, samples.length);
  const [segmentStats, setSegmentStats] = useState(null);
  const [segmentStatsLoading, setSegmentStatsLoading] = useState(false);
  const segmentRequestSeq = useRef(0);

  useEffect(() => {
    if (isFull || !samples.length || !activity?.id) {
      setSegmentStats(null);
      setSegmentStatsLoading(false);
      return undefined;
    }
    const startTime = samples[deferredStart]?.t;
    const endTime = samples[deferredEnd]?.t;
    if (!startTime || !endTime) {
      setSegmentStats(null);
      setSegmentStatsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    segmentRequestSeq.current += 1;
    const seq = segmentRequestSeq.current;
    setSegmentStats(null);
    setSegmentStatsLoading(true);

    const timer = window.setTimeout(() => {
      getActivitySegmentStats(
        activity.id,
        {
          start_time: startTime,
          end_time: endTime,
          ...(userFtp ? { ftp: userFtp } : {}),
        },
        { signal: controller.signal }
      )
        .then((data) => {
          if (!cancelled && seq === segmentRequestSeq.current) {
            setSegmentStats(data);
          }
        })
        .catch((error) => {
          if (controller.signal.aborted || error?.code === "ERR_CANCELED") {
            return;
          }
          if (!cancelled && seq === segmentRequestSeq.current) {
            setSegmentStats(null);
          }
        })
        .finally(() => {
          if (!cancelled && seq === segmentRequestSeq.current) {
            setSegmentStatsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [isFull, samples, deferredStart, deferredEnd, activity?.id, userFtp]);

  const slice = useMemo(() => {
    if (isFull || !samples.length) return null;
    return sliceForIndices(samples, deferredStart, deferredEnd);
  }, [isFull, samples, deferredStart, deferredEnd]);

  const chartData = useMemo(
    () => buildLapChartData(samples, bounds.hasDistance, slice, unitSystem),
    [samples, bounds.hasDistance, slice, unitSystem]
  );

  const hasTemperature = samples.some((s) => s.temperature_c != null);
  const hasSpeed = samples.some((s) => s.speed_ms != null);

  const chartDistanceKm = useMemo(() => {
    if (isFull) return activity.total_distance_m || 0;
    if (!bounds.hasDistance) return null;
    const { startM, endM } = distanceRangeFromIndices(
      samples,
      deferredStart,
      deferredEnd
    );
    return Math.max(0, endM - startM);
  }, [
    isFull,
    activity.total_distance_m,
    bounds.hasDistance,
    samples,
    deferredStart,
    deferredEnd,
  ]);

  const rangeSubtitle = useMemo(() => {
    if (isFullSelection(startIndex, endIndex, samples.length)) return null;
    const summary = formatRangeSummary(mode, samples, startIndex, endIndex, bounds);
    return summary.primary;
  }, [startIndex, endIndex, mode, samples, bounds]);

  const analyticsSidebarStats = useMemo(
    () =>
      resolveAnalyticsSidebarStats({
        activity,
        chartData,
        segmentStats,
        isFullRide: isFull,
        unitSystem,
      }),
    [activity, chartData, segmentStats, isFull, unitSystem]
  );

  const rangeSummary = useMemo(
    () =>
      isFullSelection(startIndex, endIndex, samples.length)
        ? null
        : formatRangeSummary(mode, samples, startIndex, endIndex, bounds),
    [startIndex, endIndex, mode, samples, bounds]
  );

  const handleRangeChange = useCallback(
    (startVal, endVal, opts = {}) => {
      if (opts.reset) {
        setStartIndex(0);
        setEndIndex(Math.max(samples.length - 1, 0));
        return;
      }
      const { startIndex: si, endIndex: ei } = indicesFromDomainValues(
        mode,
        samples,
        startVal,
        endVal,
        bounds,
        unitSystem
      );
      setStartIndex(si);
      setEndIndex(ei);
    },
    [mode, samples, bounds, unitSystem]
  );

  const handleClearRange = useCallback(() => {
    setStartIndex(0);
    setEndIndex(Math.max(samples.length - 1, 0));
  }, [samples.length]);

  const handleModeChange = useCallback((nextMode) => {
    setMode(nextMode);
  }, []);

  const gpsPoints = useMemo(
    () =>
      samples
        .filter((s) => s.lat != null && s.lon != null)
        .map((s) => [s.lat, s.lon]),
    [samples]
  );

  const highlightPoints = useMemo(() => {
    if (isFullSelection(startIndex, endIndex, samples.length)) return null;
    const pts = samples
      .slice(startIndex, endIndex + 1)
      .filter((s) => s.lat != null && s.lon != null)
      .map((s) => [s.lat, s.lon]);
    return pts.length > 1 ? pts : null;
  }, [samples, startIndex, endIndex]);

  return (
    <div className="space-y-4" data-testid="analytics-view">
      {gpsPoints.length > 1 && (
        <RouteMap points={gpsPoints} highlightPoints={highlightPoints} compact />
      )}

      <LapProfileChart
        data={chartData}
        sport={activity.sport}
        hasPower={activity.has_power && ["cycling", "running"].includes(canonicalActivitySport(activity))}
        hasHr={activity.has_hr}
        hasCadence={cadencePresentation(activity).show && activity.has_cadence}
        hasSpeed={hasSpeed}
        hasTemperature={hasTemperature}
        totalDistanceKm={chartDistanceKm}
        rangeSubtitle={rangeSubtitle}
        title="Analytics"
        testId="analytics-profile-chart"
        unitSystem={unitSystem}
        analyticsSidebarStats={analyticsSidebarStats}
      />

      {samples.length > 1 && (
        <ActivityRangeSelector
          samples={samples}
          activityStartTime={activity.start_time}
          mode={mode}
          onModeChange={handleModeChange}
          startIndex={startIndex}
          endIndex={endIndex}
          onRangeChange={handleRangeChange}
          bounds={bounds}
          unitSystem={unitSystem}
        />
      )}

      {!isFullSelection(startIndex, endIndex, samples.length) &&
        (segmentStats || segmentStatsLoading) && (
        <SegmentStatsPanel
          stats={segmentStats}
          loading={segmentStatsLoading}
          sport={activity.sport}
          rangeSummary={rangeSummary}
          onClear={handleClearRange}
          unitSystem={unitSystem}
        />
      )}

      {!bounds.hasDistance && (
        <p className="text-xs text-dark-text-secondary px-1">
          No distance data in this file — range selection uses elapsed activity time.
        </p>
      )}
    </div>
  );
};
